'use client';

import { useQuery } from '@apollo/client';
import { useRef, useEffect, useCallback } from 'react';
import gsap from 'gsap';

import { GET_VIDEOS } from '@/graphql/queries';
import { AWS_URL } from '@/constants';

const defaultImage = '';

const getImageUrl = (data) => {
  if (!data) return defaultImage;

  // Banner file structure: { bucket, cloud, path }
  if (data.path && !data.media) {
    return `${AWS_URL}${data.path}`;
  }

  // Array structure
  if (Array.isArray(data) && data.length) {
    const path = data[0]?.media?.path || data[0]?.path;
    if (path) return `${AWS_URL}${path}`;
  }

  // Nested media structure
  if (data?.media?.path) {
    return `${AWS_URL}${data.media.path}`;
  }

  return defaultImage;
};

const VideoCard = ({ src, redirectionUrl }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        entry.isIntersecting ? el.play().catch(() => {}) : el.pause();
      },
      { threshold: 0.25 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleClick = useCallback(() => {
    if (redirectionUrl) {
      window.location.href = redirectionUrl;
    } else {
      const el = videoRef.current;
      if (!el) return;
      el.paused ? el.play().catch(() => {}) : el.pause();
    }
  }, [redirectionUrl]);

  return (
    <div
      className="flex-shrink-0 w-[45vw] h-[280px] sm:w-[35vw] sm:h-[320px] md:w-[320px] md:h-[450px] lg:w-80 lg:h-[450px] rounded-xl overflow-hidden cursor-pointer shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 ease-in-out bg-black will-change-transform"
      onClick={handleClick}
    >
      <video
        ref={videoRef}
        src={src}
        className="w-full h-full object-cover block"
        loop
        muted
        playsInline
        preload="metadata"
      />
    </div>
  );
};

function MarqueeTrack({ videos }) {
  const wrapRef = useRef(null);
  const containerRef = useRef(null);
  const tweenRef = useRef(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const raf = requestAnimationFrame(() => {
      const wrapWidth = wrap.scrollWidth / 2;
      gsap.set(wrap, { x: 0 });
      tweenRef.current = gsap.to(wrap, {
        x: -wrapWidth,
        duration: 30,
        ease: 'none',
        repeat: -1,
      });
    });
    const el = containerRef.current;
    const pause = () => tweenRef.current?.pause();
    const resume = () => tweenRef.current?.play();
    el?.addEventListener('mouseenter', pause);
    el?.addEventListener('mouseleave', resume);
    return () => {
      cancelAnimationFrame(raf);
      tweenRef.current?.kill();
      el?.removeEventListener('mouseenter', pause);
      el?.removeEventListener('mouseleave', resume);
    };
  }, []);

  return (
    <div ref={containerRef} className="relative w-full overflow-hidden">
      <div ref={wrapRef} className="flex gap-3 pl-3 sm:gap-4 sm:pl-5" style={{ willChange: 'transform' }}>
        {videos.map((v, i) => <VideoCard key={`a-${i}`} src={v.src} redirectionUrl={v.redirectionUrl} />)}
        {videos.map((v, i) => <VideoCard key={`b-${i}`} src={v.src} redirectionUrl={v.redirectionUrl} />)}
      </div>
    </div>
  );
}

export default function VideoGallery() {
  const { data, loading, error } = useQuery(GET_VIDEOS, {
    variables: {
      where: {
        is_deleted: { _eq: false },
        type: { _eq: "VIDEO" }
      }
    },
    fetchPolicy: 'network-only',
  });
  console.log("vidpo",data)

  if (loading) {
    return (
      <div className="w-full bg-gray-100 py-10 px-5 min-h-[400px] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-gray-200 border-t-gray-800 rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full bg-gray-100 py-10 min-h-[400px] flex items-center justify-center">
        <p className="text-red-600">Unable to load videos. Please try again later.</p>
      </div>
    );
  }

  const videos = data?.tenant_org_web_configuration
    ?.flatMap((config) => config.web_configuration_meta)
    .filter((meta) => meta.file)
    .map((meta) => ({
      src: getImageUrl(meta.file),
      redirectionUrl: meta.redirection_url || null,
    })) ?? [];

  if (!videos.length) {
    return (
      <div className="w-full bg-gray-100 py-10 min-h-[400px] flex items-center justify-center">
        <p className="text-gray-400">No videos available at the moment.</p>
      </div>
    );
  }

  return (
    <div className="w-full py-10 overflow-hidden">
      <MarqueeTrack videos={videos} />
      <p className="text-center text-gray-400 text-xs uppercase tracking-wider mt-6">
        Hover to pause
      </p>
    </div>
  );
}